import { Shield01Icon } from '@hugeicons/core-free-icons';
import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader, EmptyState } from '@/components/common/Bits';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ProfilesTab } from '@/pages/admin/ProfilesTab';
import { TenantsTab } from '@/pages/admin/TenantsTab';
import { UserPermissionsPanel } from '@/pages/admin/UserPermissionsPanel';
import { BulkActionsPanel } from '@/pages/admin/BulkActionsPanel';
import { UserCrudPanel } from '@/pages/admin/UserCrudPanel';

export default function Admin() {
  const { can, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Tab controlled by query param ?tab=
  const VALID_TABS = ['usuarios', 'perfiles', 'ambitos'];
  const activeTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'usuarios';
  const handleTabChange = (value) => {
    if (value === 'usuarios') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: value }, { replace: true });
    }
  };

  // Bulk selection state
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  // Route catalog and profiles for bulk actions
  const [tenantRoutes, setTenantRoutes] = useState([]);
  const [profiles, setProfiles] = useState([]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (!can.admin) return;
    fetchUsers();
    api
      .get('/route-catalog')
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setTenantRoutes(data);
        } else if (data && typeof data === 'object') {
          setTenantRoutes(Object.values(data).flat());
        } else {
          setTenantRoutes([]);
        }
      })
      .catch(() => {});
    api
      .get('/admin/profiles')
      .then(({ data }) => setProfiles(data.profiles || data || []))
      .catch(() => {});
  }, [can.admin, fetchUsers]);

  // Selection helpers
  const isAllSelected = users.length > 0 && users.every((u) => selectedUserIds.includes(u.id));

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = users.map((u) => u.id);
      setSelectedUserIds((prev) => [...new Set([...prev, ...allIds])]);
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUserIds((prev) => [...prev, userId]);
    } else {
      setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
    }
  };

  const handleBulkDone = () => {
    setSelectedUserIds([]);
    fetchUsers();
  };

  if (!can.admin) {
    return (
      <EmptyState
        icon={Shield01Icon}
        title="Acceso restringido"
        subtitle="Solo el rol Administrador puede acceder a la configuración administrativa."
      />
    );
  }

  const changeRole = async (id, role) => {
    try {
      await api.put(`/admin/users/${id}/role`, { role });
      toast.success('Rol actualizado · registrado en auditoría');
      await fetchUsers();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <PageHeader
        title="Usuarios y accesos"
        subtitle="Gestión de usuarios, roles, perfiles y ámbitos organizacionales."
        testId="admin-header"
      />
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="bg-muted">
          <TabsTrigger value="usuarios" data-testid="admin-tab-usuarios">
            Usuarios y roles
          </TabsTrigger>
          <TabsTrigger value="perfiles" data-testid="admin-tab-perfiles">
            Perfiles
          </TabsTrigger>
          <TabsTrigger value="ambitos" data-testid="admin-tab-ambitos">
            Ámbitos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="usuarios" className="mt-5">
          {/* Bulk Actions Panel */}
          {selectedUserIds.length >= 2 && (
            <div className="mb-4">
              <BulkActionsPanel
                selectedUserIds={selectedUserIds}
                tenantRoutes={tenantRoutes}
                profiles={profiles}
                onDone={handleBulkDone}
              />
            </div>
          )}

          {/* User CRUD Panel — gestión completa de usuarios */}
          <UserCrudPanel
            users={users}
            onRefresh={fetchUsers}
            currentUserId={user?.id}
            selectedUserIds={selectedUserIds}
            onSelectUser={handleSelectUser}
            onSelectAll={handleSelectAll}
            isAllSelected={isAllSelected}
            onChangeRole={changeRole}
            onViewPermissions={(id) => setSelectedUserId(id)}
          />
        </TabsContent>

        <TabsContent value="perfiles" className="mt-5">
          <ProfilesTab />
        </TabsContent>

        <TabsContent value="ambitos" className="mt-5">
          <TenantsTab />
        </TabsContent>
      </Tabs>

      {selectedUserId && <UserPermissionsPanel userId={selectedUserId} onClose={() => setSelectedUserId(null)} />}
    </div>
  );
}
